<!--

 -->

<template>
  <div class="gsl-hrm-background-container">
    <div class="gsl-hrm-card-container">
      <oxd-text tag="h6" class="gsl-hrm-main-title">{{
        $t('general.edit_policy')
      }}</oxd-text>
      <oxd-divider />

      <oxd-form ref="formRef" :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-form-row>
            <oxd-grid :cols="2" class="gsl-hrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-group :label="$t('general.current_file')">
                  <oxd-alert
                    type="warn"
                    :show="true"
                    :message="currentFile ? currentFile : 'No file found'"
                    icon="file-earmark-text-fill"
                    :class="{'alert-pointer': currentFile}"
                    @click="currentFile && onClickDownload(policyId)"
                  ></oxd-alert>
                </oxd-input-group>
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
          <br />
          <oxd-grid :cols="2" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.name"
                :label="$t('general.name')"
                :rules="rules.name"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.description"
                type="textarea"
                :label="$t('general.description')"
                :rules="rules.description"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.attachment"
                type="file"
                :label="$t('general.select_file')"
                :button-label="$t('general.browse')"
                :rules="rules.attachment"
                :placeholder="$t('general.no_file_selected')"
              />
              <oxd-text class="gsl-hrm-input-hint" tag="p">
                {{ $t('general.accepts_up_to_5mb') }}
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-group
                :label="$t('general.status')"
                :classes="{wrapper: '--grouped-field'}"
              >
                <oxd-input-field
                  v-model="filters.active"
                  type="radio"
                  :option-label="$t('performance.inactive')"
                  :value="false"
                />
                <oxd-input-field
                  v-model="filters.active"
                  type="radio"
                  :option-label="$t('general.active')"
                  :value="true"
                />
              </oxd-input-group>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            type="button"
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <oxd-button
            type="button"
            :label="$t('general.draft')"
            class="policy-button-space"
            @click="onSaveDraft"
          />
          <submit-button :label="$t('general.publish')" />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import useForm from '@/core/util/composable/useForm';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {
  required,
  maxFileSize,
  validFileTypes,
  shouldNotExceedCharLength,
  shouldNotLessThanCharLength,
} from '@/core/util/validation/rules';
import {OxdAlert} from '@ohrm/oxd';

const filterModel = {
  name: '',
  description: null,
  status: null,
  active: true,
  attachment: null,
};

export default {
  components: {
    'oxd-alert': OxdAlert,
  },
  props: {
    policyId: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const {formRef, invalid, validate} = useForm();

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/policy/policies',
    );
    return {
      http,
      formRef,
      invalid,
      validate,
    };
  },

  data() {
    return {
      isLoading: false,
      currentFile: '',
      filters: {...filterModel},
      rules: {
        name: [
          required,
          shouldNotLessThanCharLength(5),
          shouldNotExceedCharLength(100),
        ],
        description: [required, shouldNotExceedCharLength(990)],
        attachment: [
          maxFileSize(5048576),
          validFileTypes([
            'text/plain',
            'text/rtf',
            'text/csv',
            'application/csv',
            'application/rtf',
            'application/pdf',
            'application/msword',
            'application/vnd.ms-excel',
            'application/vnd.ms-powerpoint',
          ]),
        ],
      },
    };
  },

  beforeMount() {
    this.isLoading = true;
    this.http
      .get(this.policyId)
      .then((response) => {
        const {data} = response.data;
        this.filters.id = data.id;
        this.filters.name = data.name;
        this.filters.description = data.description;
        this.filters.active = data.active;
        this.currentFile = data.attachment?.attachFilename;
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    onCancel() {
      navigate('/policy/viewPolicies');
    },
    onSave() {
      this.isLoading = true;
      this.http
        .update(this.policyId, {
          name: this.filters.name.trim(),
          description: this.filters.description.trim(),
          attachment: this.filters.attachment,
          status: 'Published',
          active: this.filters.active,
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.onCancel();
        });
    },
    onSaveDraft() {
      this.validate().then(() => {
        if (this.invalid === true) return;
        this.isLoading = true;

        this.http
          .update(this.policyId, {
            name: this.filters.name.trim(),
            description: this.filters.description.trim(),
            attachment: this.filters.attachment,
            status: 'Draft',
            active: this.filters.active,
          })
          .then(() => {
            return this.$toast.saveSuccess();
          })
          .then(() => {
            this.onCancel();
          });
      });
    },
    onClickDownload(id) {
      const downUrl = `${window.appGlobal.baseUrl}/policy/viewAttachment/policies/${id}`;
      window.open(downUrl, '_blank');
    },
  },
};
</script>
<style lang="scss" scoped>
.policy-button-space {
  margin-left: 10px;
}
.alert-pointer {
  cursor: pointer;
}
::v-deep(.--grouped-field) {
  display: flex;
}
</style>
